import React, { useEffect, useState, useCallback, createContext, useContext } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar, type SidebarMenuItem } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import { patientsService, PatientWithAppointments } from '../../services/patients/patientsService';
import { authService } from '../../services/auth/authService';
import './PatientDetailPage.css';

export type PatientSection = 'appointments' | 'diet-plans' | 'nutrition-profile' | 'insights' | 'messages';

const SECTION_BREADCRUMB_LABELS: Record<PatientSection, string> = {
  'appointments': 'Appointments',
  'diet-plans': 'Diet Plans',
  'nutrition-profile': 'Nutrition Profile',
  'insights': 'Insights',
  'messages': 'Messages',
};

const PatientSectionPatientContext = createContext<PatientWithAppointments | null>(null);
export const usePatientSectionPatient = () => useContext(PatientSectionPatientContext);

interface PatientSectionLayoutProps {
  section: PatientSection;
  children: React.ReactNode;
  /** Optional: pass patient from parent to avoid duplicate fetch */
  patient?: PatientWithAppointments | null;
}

export const PatientSectionLayout: React.FC<PatientSectionLayoutProps> = ({ section, children, patient: patientProp }) => {
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();
  const [patient, setPatient] = useState<PatientWithAppointments | null>(patientProp ?? null);
  const [loading, setLoading] = useState(!patientProp);
  const [error, setError] = useState<string | null>(null);

  const loadPatient = useCallback(async () => {
    if (!patientId) return;
    const numericId = Number(patientId);
    if (Number.isNaN(numericId)) {
      setError('Invalid patient ID');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await patientsService.getPatientDetails(numericId);
      setPatient(data ?? null);
      if (!data) setError('Patient not found');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load patient');
      setPatient(null);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientProp !== undefined) {
      setPatient(patientProp ?? null);
      if (patientProp !== undefined) setLoading(false);
      return;
    }
    loadPatient();
  }, [patientId, patientProp, loadPatient]);

  const handleGoToProfile = () => navigate('/profile');
  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const patientExtraChildren: SidebarMenuItem[] = [
    { id: 'patient-appointments', label: 'Appointments', icon: '', onClick: () => navigate(`/patients/${patientId}/appointments`), isActive: section === 'appointments' },
    { id: 'patient-diet-plans', label: 'Diet Plans', icon: '', onClick: () => navigate(`/patients/${patientId}/diet-plans`), isActive: section === 'diet-plans' },
    { id: 'patient-nutrition-profile', label: 'Nutrition Profile', icon: '', onClick: () => navigate(`/patients/${patientId}/nutrition-profile`), isActive: section === 'nutrition-profile' },
    { id: 'patient-insights', label: 'Insights', icon: '', onClick: () => navigate(`/patients/${patientId}/insights`), isActive: section === 'insights' },
    { id: 'patient-messages', label: 'Messages', icon: '', onClick: () => navigate(`/patients/${patientId}/messages`), isActive: section === 'messages' },
  ];

  const sidebarContext = patientId && patient
    ? { patientId, patientName: patient.name, patientExtraChildren }
    : undefined;
  const sidebarItems = createSidebarItems(navigate, window.location.pathname, sidebarContext);

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={createSidebarItems(navigate, window.location.pathname)} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="loading-message">Loading patient...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={createSidebarItems(navigate, window.location.pathname)} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="loading-message" style={{ color: '#dc2626' }}>{error || 'Patient not found'}</div>
              <button type="button" className="primary-button" style={{ marginTop: 16 }} onClick={() => navigate('/patients')}>
                Back to Patients
              </button>
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PatientSectionPatientContext.Provider value={patient}>
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="section-card">
                <nav className="detail-breadcrumb" aria-label="Breadcrumb">
                  <Link to="/patients" className="breadcrumb-link">Patients</Link>
                  <span className="breadcrumb-sep">›</span>
                  <Link to={`/patients/${patient.id}`} className="breadcrumb-link">{patient.name}</Link>
                  <span className="breadcrumb-sep">›</span>
                  <span className="breadcrumb-current">{SECTION_BREADCRUMB_LABELS[section]}</span>
                </nav>
                {children}
              </div>
            </main>
          </div>
        </div>
      </div>
    </PatientSectionPatientContext.Provider>
  );
};
